/**
 * Mis conductores (`/conductores`).
 *
 * Portado de `PortalCliente::conductores()` + `api_conductor_*`
 * (`customer_drivers`). El documento es único por cliente y sólo se fija en el
 * alta; en la edición se actualizan los datos de contacto y la licencia.
 */

import { IdCard, Pencil, Plus, RotateCcw, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Buscador } from '@/components/ui/buscador';
import { Button } from '@/components/ui/button';
import { Paginador } from '@/components/ui/paginador';
import { SectionCard } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { EstadoVacio } from '@/components/ui/estado-vacio';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { TituloPagina } from '@/components/ui/titulo-pagina';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { coincideBusqueda } from '@/lib/buscar';
import { ApiError } from '@/lib/http/api-error';

import { useGuardarConductor, useInhabilitarConductor, usePortalConductores } from '../portal.hooks';
import type { ConductorFormulario, ConductorPortal } from '../portal.types';

/** Formulario vacío. */
const VACIO: ConductorFormulario = {
  nombre: '',
  apellido: '',
  documento: '',
  telefono: '',
  licencia: '',
  tipo_licencia: '',
};

/** Tipos de licencia de conducir del Perú. */
const LICENCIAS = ['A-I', 'A-IIa', 'A-IIb', 'A-IIIa', 'A-IIIb', 'A-IIIc', 'B-I', 'B-IIa', 'B-IIb'];

/** Mensaje legible de un error de la API. */
function mensajeDe(error: unknown, porDefecto: string): string {
  return error instanceof ApiError ? error.message : porDefecto;
}

export function PortalConductoresPage() {
  useDocumentTitle('Portal · Conductores');
  const consulta = usePortalConductores();
  const guardar = useGuardarConductor();
  const darDeBaja = useInhabilitarConductor();

  const [formulario, setFormulario] = useState<ConductorFormulario | null>(null);
  const [editando, setEditando] = useState<ConductorPortal | null>(null);
  const [porBaja, setPorBaja] = useState<ConductorPortal | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  const abrirAlta = () => {
    setEditando(null);
    setFormulario({ ...VACIO });
  };

  const abrirEdicion = (conductor: ConductorPortal) => {
    setEditando(conductor);
    setFormulario({
      nombre: conductor.nombre,
      apellido: conductor.apellido,
      documento: conductor.documento,
      telefono: conductor.telefono,
      licencia: conductor.licencia,
      tipo_licencia: conductor.tipo_licencia,
    });
  };

  const enviar = () => {
    if (!formulario) return;

    guardar.mutate(
      { id: editando?.id, datos: formulario },
      {
        onSuccess: () => {
          mostrar(editando ? 'Conductor actualizado.' : 'Conductor registrado.');
          setFormulario(null);
          setEditando(null);
        },
      },
    );
  };

  const confirmarBaja = () => {
    if (!porBaja) return;

    darDeBaja.mutate(porBaja.id, {
      onSuccess: () => {
        mostrar(`${porBaja.nombre} ${porBaja.apellido} quedó dado de baja.`);
        setPorBaja(null);
      },
    });
  };

  const conductores = useMemo(() => consulta.data?.conductores ?? [], [consulta.data]);
  const activos = conductores.filter((item) => item.activo).length;

  const [busqueda, setBusqueda] = useState('');

  const filtrados = useMemo(
    () =>
      conductores.filter((conductor) =>
        coincideBusqueda(
          [
            conductor.nombre,
            conductor.apellido,
            `${conductor.apellido}, ${conductor.nombre}`,
            conductor.documento,
            conductor.telefono,
            conductor.licencia,
            conductor.tipo_licencia,
            conductor.activo ? 'activo' : 'dado de baja',
          ],
          busqueda,
        ),
      ),
    [conductores, busqueda],
  );

  const paginacion = usePaginacion(filtrados, { clave: busqueda });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <TituloPagina
          icono={IdCard}
          titulo="Mis conductores"
          descripcion="Los conductores que autorizas para retirar combustible con tu crédito."
        />
        <Button
          size="sm"
          leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
          onClick={abrirAlta}
        >
          Agregar conductor
        </Button>
      </div>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {consulta.error ? (
        <Alert variant="error">
          {mensajeDe(consulta.error, 'No se pudieron cargar tus conductores.')}
        </Alert>
      ) : null}

      <SectionCard
        title={`Conductores (${conductores.length})`}
        description={
          conductores.length
            ? `${busqueda.trim() ? `${filtrados.length} de ${conductores.length} · ` : ''}${activos} activo(s) de ${conductores.length}`
            : 'Personas autorizadas a retirar a tu nombre.'
        }
      >
        <div className="flex flex-col gap-3">
          <Buscador
            valor={busqueda}
            onChange={setBusqueda}
            ariaLabel="Buscar conductores"
            placeholder="Buscar nombre, documento o licencia…"
          />

          {consulta.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : conductores.length === 0 ? (
            <EstadoVacio
              icon={IdCard}
              titulo="Todavía no registras conductores"
              detalle="Registra a las personas que despachan a tu nombre."
              accion={
                <Button
                  size="sm"
                  leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
                  onClick={abrirAlta}
                >
                  Agregar conductor
                </Button>
              }
            />
          ) : filtrados.length === 0 ? (
            <EstadoVacio
              icon={Search}
              titulo="Sin coincidencias"
              detalle={`No encontramos conductores para “${busqueda.trim()}”.`}
              accion={
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
                  onClick={() => setBusqueda('')}
                >
                  Quitar búsqueda
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[11px] tracking-wide text-ink-400 uppercase">
                  <th className="py-2 font-semibold">Conductor</th>
                  <th className="hidden py-2 font-semibold sm:table-cell">Documento</th>
                  <th className="hidden py-2 font-semibold sm:table-cell">Teléfono</th>
                  <th className="hidden py-2 font-semibold lg:table-cell">Licencia</th>
                  <th className="py-2 font-semibold">Estado</th>
                  <th className="py-2 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginacion.visibles.map((conductor) => (
                  <tr
                    key={conductor.id}
                    className="border-b border-ink-50 transition-colors hover:bg-brand-50/40"
                  >
                    <td className="py-2 font-medium text-ink-900">
                      {conductor.apellido}, {conductor.nombre}
                    </td>
                    <td className="hidden py-2 font-mono text-ink-600 sm:table-cell">
                      {conductor.documento}
                    </td>
                    <td className="hidden py-2 text-ink-600 sm:table-cell">
                      {conductor.telefono || '—'}
                    </td>
                    <td className="hidden py-2 text-ink-600 lg:table-cell">
                      {conductor.licencia
                        ? `${conductor.licencia}${conductor.tipo_licencia ? ` (${conductor.tipo_licencia})` : ''}`
                        : '—'}
                    </td>
                    <td className="py-2">
                      {conductor.activo ? (
                        <Badge variant="success">activo</Badge>
                      ) : (
                        <Badge variant="neutral">dado de baja</Badge>
                      )}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Editar conductor"
                          onClick={() => abrirEdicion(conductor)}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {conductor.activo ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Dar de baja"
                            onClick={() => setPorBaja(conductor)}
                          >
                            <Trash2 className="size-4 text-danger-500" aria-hidden="true" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        <Paginador
          {...paginacion}
          etiqueta="conductores"
          className="-mx-5 -mb-5 mt-3 rounded-b-card"
        />
      </SectionCard>

      <Dialog
        open={formulario !== null}
        onClose={() => {
          setFormulario(null);
          setEditando(null);
        }}
        title={editando ? `Editar ${editando.nombre} ${editando.apellido}` : 'Nuevo conductor'}
        description={
          editando
            ? 'El número de documento no se puede cambiar; actualiza los demás datos.'
            : 'El documento identifica al conductor en los despachos.'
        }
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFormulario(null);
                setEditando(null);
              }}
            >
              Cancelar
            </Button>
            <Button size="sm" loading={guardar.isPending} onClick={enviar}>
              {editando ? 'Guardar cambios' : 'Registrar conductor'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Nombres"
              value={formulario?.nombre ?? ''}
              onChange={(evento) =>
                setFormulario((previo) => ({ ...(previo ?? VACIO), nombre: evento.target.value }))
              }
            />
            <Input
              label="Apellidos"
              value={formulario?.apellido ?? ''}
              onChange={(evento) =>
                setFormulario((previo) => ({ ...(previo ?? VACIO), apellido: evento.target.value }))
              }
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Documento (DNI)"
              value={formulario?.documento ?? ''}
              disabled={editando !== null}
              hint={editando ? 'No se puede cambiar.' : undefined}
              onChange={(evento) =>
                setFormulario((previo) => ({ ...(previo ?? VACIO), documento: evento.target.value }))
              }
            />
            <Input
              label="Teléfono"
              value={formulario?.telefono ?? ''}
              onChange={(evento) =>
                setFormulario((previo) => ({ ...(previo ?? VACIO), telefono: evento.target.value }))
              }
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="N° de licencia"
              value={formulario?.licencia ?? ''}
              onChange={(evento) =>
                setFormulario((previo) => ({ ...(previo ?? VACIO), licencia: evento.target.value }))
              }
            />
            <div className="flex flex-col gap-1.5">
              <label htmlFor="portal-licencia" className="text-sm font-medium text-ink-700">
                Tipo de licencia
              </label>
              <Select
                id="portal-licencia"
                value={formulario?.tipo_licencia ?? ''}
                onChange={(evento) =>
                  setFormulario((previo) => ({ ...(previo ?? VACIO), tipo_licencia: evento.target.value }))
                }
              >
                <option value="">—</option>
                {LICENCIAS.map((licencia) => (
                  <option key={licencia} value={licencia}>
                    {licencia}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {guardar.error ? (
            <Alert variant="error">
              {mensajeDe(
                guardar.error,
                editando ? 'No se pudo actualizar el conductor.' : 'No se pudo registrar el conductor.',
              )}
            </Alert>
          ) : null}
        </div>
      </Dialog>

      <ConfirmDialog
        open={porBaja !== null}
        title="Dar de baja el conductor"
        description={
          porBaja
            ? `${porBaja.nombre} ${porBaja.apellido} dejará de aparecer como activo, pero su historial se conserva.`
            : ''
        }
        confirmLabel="Dar de baja"
        loading={darDeBaja.isPending}
        onConfirm={confirmarBaja}
        onClose={() => setPorBaja(null)}
      />
    </div>
  );
}
